import React, { useEffect, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { CloudUploadOutlined } from "@ant-design/icons";
import { Modal, Upload, Slider, Button, message, Spin } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { useFileDelete, useFileSave } from "../hooks/api/file/hooks";
import { getCroppedImg } from "./utils";

const { Dragger } = Upload;

interface UploadFoodImageProps {
  maxUploadCount?: number;
  text?: string;
  value?: string | string[];
  onChange?: (urls: string | string[]) => void;
  disableRemoveFirst?: boolean;
  bannerAspect?: number;
  accept?:string,
  isCropTrue?: boolean;
}

const UploadFoodImage: React.FC<UploadFoodImageProps> = ({
  maxUploadCount = 2,
  text = "Drag & drop files here",
  accept= "image/*",
  value,
  onChange,
  disableRemoveFirst = false,
  isCropTrue = true,
  bannerAspect = 1,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");

  // Cropper states
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [isCrop, setIsCrop] = useState(false)

  const { mutate: save, isPending } = useFileSave({
    onSuccess: (res) => {
      setFileList((prev) => {
        const newFileList = [
          ...prev,
          { uid: res.data._id, name: res.data.name, url: res.data.url },
        ];
        const urls = newFileList.map((x) => x.url as string);
        onChange?.(maxUploadCount === 1 ? urls[0] : urls);
        return newFileList;
      });
      messageApi.success("Uploaded successfully");
    },
    onError: () => messageApi.error("Upload failed"),
  });

  const { mutate: remove } = useFileDelete({
    onSuccess: () => messageApi.success("Deleted successfully"),
    onError: () => messageApi.error("Delete failed"),
  });

  /** 🔧 Fix: Respect string vs array on initial load */
  useEffect(() => {
    if (!value) {
      setFileList([]);
      return;
    }

    let normalizedArray: string[] = [];
    if (Array.isArray(value)) {
      normalizedArray = value;
    } else {
      normalizedArray = [value];
    }

    const urlsInState = fileList.map((f) => f.url).filter(Boolean);
    const isSame = JSON.stringify(urlsInState) === JSON.stringify(normalizedArray);
    if (!isSame) {
      const initialFiles = normalizedArray.map((url, i) => ({
        uid: `init-${i}`,
        name: `Image-${i + 1}`,
        status: "done" as UploadFile["status"],
        url,
      }));
      setFileList(initialFiles);
    }
  }, [value]);

  /** Prevent invalid or over-limit uploads */
 const beforeUpload = (file: File) => {
  if (fileList.length >= maxUploadCount) {
    messageApi.warning(`You can upload only ${maxUploadCount} file(s).`);
    return Upload.LIST_IGNORE;
  }

  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");

  const isValidSize = (isImage && file.size / 1024 / 1024 < 5) || 
                      (isVideo && file.size / 1024 / 1024 < 30);

  if (!isImage && !isVideo) messageApi.error("Only image or video files allowed!");
  if (!isValidSize) messageApi.error(
    isImage ? "Image must be smaller than 5MB!" : "Video must be smaller than 30MB!"
  );

  return isValidSize && (isImage || isVideo);
};


  const handleChange: UploadProps["onChange"] = ({ file, fileList: newFileList }) => {
  // CASE 1: File Removed (AntD triggers onChange)
  if (file.status === "removed") {
    setFileList(newFileList); // only update remaining items
    const urls = newFileList.map((x) => x.url as string);
    onChange?.(maxUploadCount === 1 ? urls[0] || "" : urls);
    return;
  }

  // CASE 2: Newly added file
  const latest = newFileList[newFileList.length - 1];

  if (latest?.originFileObj && beforeUpload(latest.originFileObj)) {
    // --- CROP MODE ---
    if (isCropTrue) {
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCropFile(latest.originFileObj);
      setCropModalVisible(true);
      return;
    }

    // --- NORMAL UPLOAD ---
    const formData = new FormData();
    formData.append("file", latest.originFileObj);
    save(formData);
    return;
  }

  // Default fallback (rare)
  setFileList(newFileList);
  const urls = newFileList.map((x) => x.url as string);
  onChange?.(maxUploadCount === 1 ? urls[0] : urls);
};


  const handleCropComplete = useCallback((_: any, area: any) => {
    setCroppedAreaPixels(area);
  }, []);

  const handleCropConfirm = async () => {
    if (cropFile && croppedAreaPixels) {
      try {
        setIsCrop(true)
        const croppedBlob = await getCroppedImg(cropFile, croppedAreaPixels);
        const croppedFile = new File([croppedBlob], cropFile.name, {
          type: cropFile.type,
        });
        const formData = new FormData();
        formData.append("file", croppedFile);
        save(formData);
      } catch {
        messageApi.error("Cropping failed");
        setIsCrop(false)
      } finally {
         setIsCrop(false)
      }
    }
    setCropModalVisible(false);
    setCropFile(null);
  };

  const handleRemove = (file: UploadFile) => {
    if (file.url) remove({ url: file.url });
    setFileList((prev) => {
      const updated = prev.filter((f) => f.uid !== file.uid);
      const urls = updated.map((x) => x.url as string);
      onChange?.(maxUploadCount === 1 ? urls[0] || "" : urls);
      return updated;
    });
  };

  const handlePreview = (file: UploadFile) => {
    setPreviewImage(file.url || "");
    setPreviewTitle(file.name || "Preview");
    setPreviewVisible(true);
  };

  const uploadDisabled = fileList.length >= maxUploadCount;

  const handleSaveWithoutCrop = () => {
  if (cropFile) {
    const formData = new FormData();
    formData.append("file", cropFile); // upload original image
    save(formData);
  }
  setCropModalVisible(false);
  setCropFile(null);
};


  return (
    <>
      {contextHolder}
    
      <Dragger
        multiple={false}
        accept={accept}
        fileList={fileList}
        listType="picture"
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onRemove={handleRemove}
        onPreview={handlePreview}
        showUploadList={{
          showRemoveIcon: (file) =>
            !(disableRemoveFirst && fileList[0]?.uid === file.uid),
          showPreviewIcon: true,
        }}
        disabled={isPending}
      >
         {isPending && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20">
              <Spin size="large" />
            </div>
          )}
        {!uploadDisabled ? (
          <div className="flex flex-col items-center justify-center py-5">
            <CloudUploadOutlined style={{ fontSize: "20px", color: "#A6A6A6" }} />
            <p className="text-gray-500 mt-2">{text}</p>
            <p className="text-gray-500 mt-2">
              Or click to browse (max {maxUploadCount} file
              {maxUploadCount > 1 ? "s" : ""}, up to 5MB each)
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-5 opacity-60 cursor-not-allowed select-none">
            <CloudUploadOutlined style={{ fontSize: "20px", color: "#A6A6A6" }} />
            <p className="text-gray-400 mt-2">Upload limit reached</p>
            <p className="text-gray-400 mt-2">Remove an image to upload new</p>
          </div>
        )}
      </Dragger>

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img
          alt="preview"
          style={{ width: "100%", objectFit: "contain" }}
          src={previewImage}
        />
      </Modal>

      {/* Crop Modal */}
      <Modal
        open={cropModalVisible}
        title="Crop Image"
        footer={[
          <Button key="cancel" onClick={() => setCropModalVisible(false)}>
            Cancel
          </Button>,
           <Button key="no-crop" onClick={handleSaveWithoutCrop}>
            Save Without Crop
          </Button>,
          <Button type="primary" key="ok" onClick={handleCropConfirm} disabled={isCrop}>
            Crop & Upload
          </Button>,
        ]}
        onCancel={() => setCropModalVisible(false)}
        width={600}
      >
        {cropFile && (
          <div style={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper
              image={URL.createObjectURL(cropFile)}
              crop={crop}
              zoom={zoom}
              aspect={bannerAspect || 1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          </div>
        )}
        <Slider
          min={1}
          max={3}
          step={1}
          value={zoom}
          onChange={setZoom}
          style={{ marginTop: 16 }}
        />
      </Modal>
    </>
  );
};

export default UploadFoodImage;
